import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTrimDrags } from '../hooks/useTrimDrags';
import { classnames } from '@/libs/utils';
import { Clip } from '@/domains/asset';
import { CustomEvents } from '@/libs/eventBus/constants';
import EventBus from '@/libs/eventBus/eventBus';
import useThrottle from '@/hooks/useThrottle';
import { useRemotionPlayerPause } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerPause';
import { isClipInOriginalLanguage } from '@/libs/clipUtils';

export interface BaseTrimHandlesProps {
  /** Clip frame rate */
  fps: number;
  /** Full clip object, used to check translation status */
  clipData: Clip;
  /** Which handle to reveal on hover */
  showTrimsBars: 'start' | 'end' | null;
  /** Id of the segment being trimmed */
  segmentId: string;
  /** Current left-handle drag offset in seconds */
  dragStart: number;
  /** Current right-handle drag offset in seconds */
  dragEnd: number;
  /** Setter for left-handle drag offset */
  setDragStart: (value: number) => void;
  /** Setter for right-handle drag offset */
  setDragEnd: (value: number) => void;
  /** Drop zone boundary for drag validation */
  newTimelineContainerRef: React.RefObject<HTMLDivElement>;
  /** Parent container used for edge-scroll during drag */
  speakerTimelineContainerRef: React.RefObject<HTMLDivElement>;
  /** Segment start frame, used for scroll tracking during drag */
  currentBoundsFromFrame: number;
  /** Segment end frame, used for scroll tracking during drag */
  currentBoundsToFrame: number;
  /** 'always' resets on every bound change, 'skip-during-drag' waits until drag ends */
  resetMode: 'always' | 'skip-during-drag';
  /** Segment start value whose change triggers drag state reset */
  segmentStartBound: number;
  /** Segment end value whose change triggers drag state reset */
  segmentEndBound: number;
  /** Extra reset logic when bounds change (e.g. clear drag id) */
  onBoundsChanged?: () => void;
  /** Called when a trim drag begins (after auto-pause) */
  onDragStarted?: () => void;
  /** Called when left-handle drag ends with final offset */
  onStartDragEnd: (offset: number) => void;
  /** Called when right-handle drag ends with final offset */
  onEndDragEnd: (offset: number) => void;
  /** Allowed drag range for left handle */
  getLeftTrimBounds: () => { maxOffset: number; minOffset: number };
  /** Allowed drag range for right handle */
  getRightTrimBounds: () => { maxOffset: number; minOffset: number };
  /** Called each frame during drag (e.g. for delete-zone preview) */
  onDragUpdate?: (dragValue: number, baseOffset: number, isStartHandle: boolean) => void;
  /** Called when both handles are idle (e.g. to clear delete-zone preview) */
  onDragIdle?: () => void;
}

export default function BaseTrimHandles({
  fps,
  clipData,
  showTrimsBars,
  segmentId,
  dragStart,
  dragEnd,
  setDragStart,
  setDragEnd,
  newTimelineContainerRef,
  speakerTimelineContainerRef,
  currentBoundsFromFrame,
  currentBoundsToFrame,
  resetMode,
  segmentStartBound,
  segmentEndBound,
  onBoundsChanged,
  onDragStarted,
  onStartDragEnd,
  onEndDragEnd,
  getLeftTrimBounds,
  getRightTrimBounds,
  onDragUpdate,
  onDragIdle
}: BaseTrimHandlesProps): React.ReactElement {
  const dragCleanupRef = useRef<() => void>(() => {});
  const { pause, isPlaying } = useRemotionPlayerPause();
  const throttledForceScrollEvent = useThrottle(
    (time: number) =>
      EventBus.dispatch(CustomEvents.TimelineForcedScroll, {
        time,
        segmentId
      }),
    100
  );

  const isClipTranslated = useMemo(() => {
    return !isClipInOriginalLanguage(clipData);
  }, [clipData]);

  const onBoundsChangedRef = useRef(onBoundsChanged);
  onBoundsChangedRef.current = onBoundsChanged;
  const onDragUpdateRef = useRef(onDragUpdate);
  onDragUpdateRef.current = onDragUpdate;
  const onDragIdleRef = useRef(onDragIdle);
  onDragIdleRef.current = onDragIdle;
  const onStartDragEndRef = useRef(onStartDragEnd);
  onStartDragEndRef.current = onStartDragEnd;
  const onEndDragEndRef = useRef(onEndDragEnd);
  onEndDragEndRef.current = onEndDragEnd;
  const onDragStartedRef = useRef(onDragStarted);
  onDragStartedRef.current = onDragStarted;

  useEffect(() => {
    return () => {
      dragCleanupRef.current();
    };
  }, []);

  useEffect(() => {
    if (resetMode !== 'always') {
      return;
    }
    onBoundsChangedRef.current?.();
    dragCleanupRef.current();
    dragCleanupRef.current = () => {};
  }, [segmentStartBound, segmentEndBound, resetMode]);

  useEffect(() => {
    if (resetMode !== 'skip-during-drag') {
      return;
    }
    if (dragStart || dragEnd) {
      return;
    }
    dragCleanupRef.current();
    dragCleanupRef.current = () => {};
  }, [segmentStartBound, segmentEndBound, dragStart, dragEnd, resetMode]);

  const handleDragStart = useCallback(
    (_dragId: string | null) => {
      if (isPlaying) {
        pause();
      }
      onDragStartedRef.current?.();
    },
    [isPlaying, pause]
  );

  const stableOnStartDragEnd = useCallback((offset: number) => {
    onStartDragEndRef.current(offset);
  }, []);

  const stableOnEndDragEnd = useCallback((offset: number) => {
    onEndDragEndRef.current(offset);
  }, []);

  const { onMouseDown: onStartDrag } = useTrimDrags({
    setDraggingElement: handleDragStart,
    setDraggingElementOffset: setDragStart,
    draggingElementOffset: dragStart,
    onDragEnd: stableOnStartDragEnd,
    droppableContainerRef: newTimelineContainerRef,
    shouldFixDraggableElement: true,
    draggableElementParentRef: speakerTimelineContainerRef,
    fps
  });

  const { onMouseDown: onEndDrag } = useTrimDrags({
    setDraggingElement: handleDragStart,
    setDraggingElementOffset: setDragEnd,
    draggingElementOffset: dragEnd,
    onDragEnd: stableOnEndDragEnd,
    droppableContainerRef: newTimelineContainerRef,
    shouldFixDraggableElement: true,
    draggableElementParentRef: speakerTimelineContainerRef,
    isDragFromEnd: true,
    fps
  });

  useEffect(() => {
    const fromFrameTime = currentBoundsFromFrame / fps;
    const toFrameTime = currentBoundsToFrame / fps;

    if (dragStart) {
      const adjustedTime = fromFrameTime + dragStart;
      onDragUpdateRef.current?.(dragStart, fromFrameTime, true);
      throttledForceScrollEvent(adjustedTime);
    }

    if (dragEnd) {
      const adjustedTime = toFrameTime + dragEnd;
      onDragUpdateRef.current?.(dragEnd, toFrameTime, false);
      throttledForceScrollEvent(adjustedTime);
    }

    if (!dragStart && !dragEnd) {
      onDragIdleRef.current?.();
    }
  }, [currentBoundsFromFrame, currentBoundsToFrame, segmentId, dragEnd, dragStart, fps, throttledForceScrollEvent]);

  const onLeftTrimMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (isClipTranslated) {
        return;
      }
      const { maxOffset, minOffset } = getLeftTrimBounds();
      const cleanup = onStartDrag(e, 'start', maxOffset, minOffset);
      dragCleanupRef.current = cleanup;
    },
    [isClipTranslated, getLeftTrimBounds, onStartDrag]
  );

  const onRightTrimMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (isClipTranslated) {
        return;
      }
      const { maxOffset, minOffset } = getRightTrimBounds();
      const cleanup = onEndDrag(e, 'end', maxOffset, minOffset);
      dragCleanupRef.current = cleanup;
    },
    [isClipTranslated, getRightTrimBounds, onEndDrag]
  );

  return (
    <div
      data-testid="trim-handles-wrapper"
      className="transition-opacity duration-300"
      style={{
        opacity: showTrimsBars || dragStart || dragEnd ? 1 : 0
      }}
    >
      <div
        data-testid="left-trim-handle"
        className={classnames(
          'absolute inset-y-0 left-0 z-20 flex w-[8px] cursor-ew-resize rounded rounded-l-md rounded-r-none bg-slate-700',
          showTrimsBars === 'start' || dragStart ? 'opacity-100' : 'opacity-0',
          isClipTranslated ? 'cursor-not-allowed' : ''
        )}
        onMouseDown={onLeftTrimMouseDown}
        onClick={e => {
          e.stopPropagation();
        }}
      ></div>
      <div
        data-testid="right-trim-handle"
        className={classnames(
          'absolute inset-y-0 right-0 z-20 flex w-[8px] cursor-ew-resize rounded rounded-l-none rounded-r-md bg-slate-700',
          showTrimsBars === 'end' || dragEnd ? 'opacity-100' : 'opacity-0',
          isClipTranslated ? 'cursor-not-allowed' : ''
        )}
        onMouseDown={onRightTrimMouseDown}
        onClick={e => {
          e.stopPropagation();
        }}
      ></div>
    </div>
  );
}
